"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../../config"
import "./AdminDashboard.css"

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFarms: 0,
    totalDevices: 0,
    totalCalculations: 0,
  })
  const [recentUsers, setRecentUsers] = useState([])
  const [recentCalculations, setRecentCalculations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Add inside the component, below useState declarations
  const [selectedUser, setSelectedUser] = useState(null)

  const handleViewUser = async (userId) => {
    try {
      const userRes = await axios.get(`${API_URL}/api/admin/users/${userId}`)
      setSelectedUser(userRes.data)
      console.log("User details:", userRes.data) // or show in modal
    } catch (err) {
      console.error("Error fetching user:", err)
    }
  }


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        // Fetch dashboard statistics
        const statsRes = await axios.get(`${API_URL}/api/admin/stats`)
        setStats(statsRes.data)

        // Fetch recent users
        const usersRes = await axios.get(`${API_URL}/api/admin/users?limit=5&sort=date`)
        // Ensure recentUsers is always an array
        setRecentUsers(Array.isArray(usersRes.data) ? usersRes.data : [])

        // Fetch recent calculations
        const calcRes = await axios.get(`${API_URL}/api/admin/calculations?limit=5&sort=createdAt`)

        // Ensure recentCalculations is always an array
        setRecentCalculations(Array.isArray(calcRes.data.calculations) ? calcRes.data.calculations : [])

        setLoading(false)
      } catch (err) {
        setError("Failed to load admin dashboard data")
        setLoading(false)
        console.error(err)
      }
    }

    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    )
  }

  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard-header">
        <h1>Admin Dashboard</h1>
        <p className="admin-subtitle">System overview and management</p>
      </div>

      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-icon user-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalUsers}</h3>
            <p>Registered Users</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon farm-icon">
            <i className="fas fa-tractor"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalFarms}</h3>
            <p>Farms</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon device-icon">
            <i className="fas fa-microchip"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalDevices}</h3>
            <p>Devices</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon calc-icon">
            <i className="fas fa-calculator"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalCalculations}</h3>
            <p>Calculations</p>
          </div>
        </div>
      </div>

      <div className="admin-content-grid">
        <div className="admin-card users-card">
          <div className="card-header">
            <h2>Recent Users</h2>
            <Link to="/admin/users" className="card-action-link">
              View All
            </Link>
          </div>
          <div className="card-body">
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.length > 0 ? (
                    recentUsers.map((user) => (
                      <tr key={user._id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{new Date(user.date).toLocaleDateString()}</td>
                        <td>
                          <div className="table-actions">
                            <Link to={`/admin/users/${user._id}`} className="table-action-button">
                              <i className="fas fa-eye"></i>
                            </Link>
                            <button className="table-action-button">
                              <i className="fas fa-edit"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="no-data">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="admin-card calculations-card">
          <div className="card-header">
            <h2>Recent Calculations</h2>
            <Link to="/admin/calculations" className="card-action-link">
              View All
            </Link>
          </div>
          <div className="card-body">
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Date</th>
                    <th>Location</th>
                    <th>Models</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalculations.length > 0 ? (
                    recentCalculations.map((calc) => (
                      <tr key={calc._id}>
                        <td>{calc.user ? calc.user.name : "Guest"}</td>
                        <td>{new Date(calc.date).toLocaleDateString()}</td>
                        <td>
                          {calc.location?.district?.name && calc.location?.country?.name
                            ? `${calc.location.district.name}, ${calc.location.country.name}`
                            : calc.location?.coordinates
                              ? `${calc.location.coordinates.lat.toFixed(2)}, ${calc.location.coordinates.lng.toFixed(2)}`
                              : "N/A"}
                        </td>
                        <td>{calc.models ? calc.models.length : 0} models</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="no-data">
                        No calculations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-actions-grid">
        <div className="admin-card actions-card">
          <div className="card-header">
            <h2>Quick Actions</h2>
          </div>
          <div className="card-body">
            <div className="admin-actions">
              <Link to="/admin/master-data" className="admin-action-item">
                <div className="admin-action-icon">
                  <i className="fas fa-database"></i>
                </div>
                <div className="admin-action-content">
                  <h3>Manage Master Data</h3>
                  <p>Manage countries, districts, soil types, and more</p>
                </div>
              </Link>

              <Link to="/admin/users" className="admin-action-item">
                <div className="admin-action-icon">
                  <i className="fas fa-user-cog"></i>
                </div>
                <div className="admin-action-content">
                  <h3>User Management</h3>
                  <p>Manage user accounts and permissions</p>
                </div>
              </Link>

              <Link to="/admin/weather" className="admin-action-item">
                <div className="admin-action-icon">
                  <i className="fas fa-cloud-sun"></i>
                </div>
                <div className="admin-action-content">
                  <h3>Weather Data</h3>
                  <p>Manage and import weather data</p>
                </div>
              </Link>

              <Link to="/admin/system" className="admin-action-item">
                <div className="admin-action-icon">
                  <i className="fas fa-cogs"></i>
                </div>
                <div className="admin-action-content">
                  <h3>System Settings</h3>
                  <p>Configure application settings</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
